import { __decorate } from "tslib";
import { LitElement } from "lit";
import { property } from "lit/decorators.js";
import { isSymbol } from "sharekit/is.js";
import { GodownConfig } from "./config.js";
import { deepQuerySelector, deepQuerySelectorAll, Events, Observers, Timeouts } from "./tools/index.js";
export const attributeName = (property, { attribute }) => {
    if (attribute === true || attribute === undefined) {
        return String(property).toLowerCase();
    }
    if (attribute === false || isSymbol(property)) {
        return;
    }
    return attribute;
};
const withSelectorRegex = /^[\s\S]*{[\s\S]*}$/;
const SLOT = "slot";
export class GodownElement extends LitElement {
    /**
     * Defines a custom element with the specified tag name and options.
     * If the element is not already defined, it will be registered with the GodownConfig.
     * @param tagName - The tag name to use for the custom element, defaulting to the {@link elementTagName} static property.
     * @param options - Element definition options.
     */
    static define(tagName = this.elementTagName, options) {
        if (!this.isDefined()) {
            this.godownConfig.define(tagName, this, options);
        }
    }
    static isDefined() {
        return !!this.getDefined();
    }
    static getDefined() {
        return this.godownConfig.get(this.elementTagName);
    }
    static finalize() {
        super.finalize();
        this.elementAttributes = new Map();
        for (const [property, option] of this.elementProperties.entries()) {
            const attribute = attributeName(property, option);
            if (attribute) {
                this.elementAttributes.set(attribute, property);
            }
        }
    }
    /**
     * Returns an object containing the current values of the observed attributes.
     * @returns An object where the keys are the observed attribute names and the values are the current values of those attributes.
     */
    get observedRecord() {
        const record = {};
        for (const [attribute, property] of this.constructor.elementAttributes.entries()) {
            record[attribute] = this[property];
        }
        return record;
    }
    /**
     * Returns the first unnamed slot element in the shadow root.
     * @returns The unnamed slot element, or `null` if not found.
     */
    get _slot() {
        return this.shadowRoot ? this.shadowRoot.querySelector(`${SLOT}:not([name])`) : null;
    }
    /**
     * Returns all slot elements in the shadow root.
     * @returns An array of all slot elements in the shadow root.
     */
    get _slotAll() {
        return this.shadowRoot ? [...this.shadowRoot.querySelectorAll(SLOT)] : [];
    }
    /**
     * Returns all slotted elements.
     * @returns An array of all slotted elements.
     */
    get _slottedAll() {
        return [...this.querySelectorAll(`[${SLOT}]`)];
    }
    /**
     * Returns names of all slotted elements.
     * @returns An array of slot names.
     */
    get _slottedNames() {
        return this._slottedAll.map((c) => c.getAttribute(SLOT)).filter((v) => v);
    }
    /**
     * Returns the bounding client rectangle of the element or its contents root.
     * If the element has the `contents` property set, it will return the bounding client rectangle of the first element in the shadow root or the `contentsRoot` element, instead of the element itself.
     * @returns The bounding client rectangle of the element or its contents root.
     */
    getBoundingClientRect() {
        let root;
        return this.contents &&
            // root is contentsRoot or first Element of shadowRoot
            (root = this.contentsRoot || this.shadowRoot?.firstElementChild) &&
            // root is not the element itself
            root !== this
            ? root.getBoundingClientRect()
            : super.getBoundingClientRect();
    }
    /**
     * Returns the client rects of the element or its contents root.
     * If the element has the `contents` property set, it will return the client rects of the first element in the shadow root or the `contentsRoot` element, instead of the element itself.
     * @returns The client rects of the element or its contents root.
     */
    getClientRects() {
        let root;
        return this.contents &&
            // root is contentsRoot or first Element of shadowRoot
            (root = this.contentsRoot || this.shadowRoot?.firstElementChild) &&
            // root is not the element itself
            root !== this
            ? root.getClientRects()
            : super.getClientRects();
    }
    /**
     * Returns the stylex property value.
     */
    get stylex() {
        return this.__stylex.css;
    }
    /**
     * Appends to `shadowRoot.adoptedStyleSheets` and overrides the initial styles.
     *
     * If there is no selector, it will be `:host`.
     *
     * If element has no shadowRoot, it will be applied after connectedCallback.
     *
     * Resetting will remove the previous stylex.
     *
     * @parma sx CSS string.
     *
     * @example
     * ```html
     * <custom-element stylex=":host{--key:value;}"></custom-element>
     * <custom-element stylex="--key:value;"></custom-element>
     * ```
     */
    set stylex(sx) {
        sx = sx.trim();
        if (!withSelectorRegex.test(sx)) {
            sx = `:host{${sx}}`;
        }
        if (!this.shadowRoot) {
            this.__stylex.lazy = sx;
            return;
        }
        if (this.__stylex.index) {
            this.shadowRoot.adoptedStyleSheets.splice(this.__stylex.index, 1);
        }
        this.__stylex.css = sx;
        this.__stylex.index = this.adoptStyles(sx);
    }
    constructor(init) {
        super();
        this.events = new Events();
        this.observers = new Observers();
        this.timeouts = new Timeouts();
        this.__stylex = {};
        this.assign = {
            ...(GodownElement.godownConfig?.assign || {}),
            ...init,
        };
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.assign) {
            Object.assign(this, this.assign);
            this.assign = null;
        }
        const { lazy } = this.__stylex;
        if (lazy) {
            this.adoptStyles(lazy);
            this.__stylex.lazy = "";
        }
    }
    disconnectedCallback() {
        this.events.removeAll();
        this.observers.removeAll();
        this.timeouts.removeAll();
    }
    /**
     * Performs a deep query selector on the current element, searching within its shadow DOM if present.
     *
     * @param selectors - The CSS selectors to use for the query.
     * @returns The first element that matches the specified selectors, or null if no matches are found.
     */
    deepQuerySelector(selectors) {
        return deepQuerySelector(selectors, this);
    }
    /**
     * Performs a deep query selector all on the current element, searching within its shadow DOM if present.
     *
     * @param selectors - The CSS selectors to use for the query.
     * @returns An array of all elements that match the specified selectors.
     */
    deepQuerySelectorAll(selectors) {
        return deepQuerySelectorAll(selectors, this);
    }
    /**
     * Adds the provided CSS styles to the element's shadow DOM adopted style sheets.
     *
     * @param styles - An array of CSS strings to be added to the adopted style sheets.
     * @returns The index of the injected style sheet, or `undefined` if there is no shadow root or no styles were provided.
     */
    adoptStyles(...styles) {
        if (!this.shadowRoot || !styles.length) {
            return;
        }
        const stack = this.shadowRoot.adoptedStyleSheets;
        const sheet = new CSSStyleSheet();
        styles.forEach((style) => sheet.insertRule(style.toString()));
        stack.push(sheet);
        return stack.length - 1;
    }
    dispatchCustomEvent(type, detail, options) {
        this.dispatchEvent(new CustomEvent(type, { detail, composed: true, ...options }));
    }
}
GodownElement.godownConfig = new GodownConfig();
__decorate([
    property({ type: Boolean, reflect: true })
], GodownElement.prototype, "contents", void 0);
__decorate([
    property({ reflect: true })
], GodownElement.prototype, "stylex", null);
//# sourceMappingURL=element.js.map